import React from 'react';
import { useData } from '../contexts/DataContext';
import { MetricCard } from '../components/dashboard/MetricCard';
import { RequestsChart } from '../components/dashboard/RequestsChart';
import { StatusBadge, ResponseModeBadge } from '../components/dashboard/StatusBadge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Activity, 
  Clock, 
  CheckCircle2, 
  TrendingUp, 
  RefreshCw,
  AlertCircle,
  Database,
  Zap
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const Dashboard = () => {
  const { data, refreshData, isRefreshing } = useData();
  const { metrics, providers, recentRequests, alerts } = data;

  const handleRefresh = async () => {
    await refreshData();
    toast.success('Dashboard refreshed successfully');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Real-time monitoring of RateHub shipping rate engine
          </p>
        </div>
        <Button onClick={handleRefresh} disabled={isRefreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* System Status Alert */}
      <Card className="border-success bg-success-light/10">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-success-foreground" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">All Systems Operational</h3>
              <p className="text-sm text-muted-foreground">All providers are healthy and responding normally</p>
            </div>
            <Badge variant="outline" className="bg-background">
              {metrics.uptime}% Uptime
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Requests"
          value={metrics.totalRequests.toLocaleString()}
          icon={Activity}
          trend="up"
          trendValue="+12.3%"
        />
        <MetricCard
          title="Avg Latency"
          value={metrics.avgLatency}
          suffix="ms"
          icon={Clock}
          trend="down"
          trendValue="-8.5%"
        />
        <MetricCard
          title="Cache Hit Rate"
          value={metrics.cacheHitRate}
          suffix="%"
          icon={Database}
          trend="up"
          trendValue="+3.2%"
        />
        <MetricCard
          title="Success Rate"
          value={metrics.successRate}
          suffix="%"
          icon={CheckCircle2}
          trend="up"
          trendValue="+0.5%"
        />
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        <RequestsChart />
        
        {/* Provider Health */}
        <Card>
          <CardHeader>
            <CardTitle>Provider Health</CardTitle>
            <CardDescription>Current status of shipping rate providers</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {providers.map((provider) => (
              <div key={provider.id} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-accent/50 transition-smooth">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium">{provider.name}</h4>
                    <StatusBadge status={provider.status} />
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>Success: {provider.successRate}%</span>
                    <span>Latency: {provider.avgLatency}ms</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">{provider.requestsToday}</div>
                  <div className="text-xs text-muted-foreground">requests</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Recent Alerts */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Alerts</CardTitle>
            <CardDescription>Latest system notifications</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {alerts.slice(0, 5).map((alert) => {
                const iconMap = {
                  warning: AlertCircle,
                  info: Activity,
                  success: CheckCircle2,
                };
                const Icon = iconMap[alert.type];
                const colorMap = {
                  warning: 'text-warning',
                  info: 'text-primary',
                  success: 'text-success',
                };

                return (
                  <div key={alert.id} className="flex gap-3 p-3 border border-border rounded-lg">
                    <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${colorMap[alert.type]}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{alert.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {format(new Date(alert.timestamp), 'MMM d, h:mm a')}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recent Requests */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Requests</CardTitle>
            <CardDescription>Latest rate calculations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentRequests.slice(0, 5).map((request) => (
                <div key={request.id} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-accent/50 transition-smooth">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{request.destination}</span>
                      <ResponseModeBadge mode={request.responseMode} />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{request.provider}</span>
                      <span>•</span>
                      <span>{request.latency}ms</span>
                      {request.cacheHit && (
                        <>
                          <span>•</span>
                          <Badge variant="outline" className="text-xs px-1 py-0">Cached</Badge>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold">{request.cartValue} SAR</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Metrics</CardTitle>
          <CardDescription>Key performance indicators</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 border border-border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">P95 Latency</span>
              </div>
              <div className="text-2xl font-bold">{metrics.p95Latency}ms</div>
              <p className="text-xs text-muted-foreground mt-1">95th percentile response time</p>
            </div>
            <div className="p-4 border border-border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-4 w-4 text-warning" />
                <span className="text-sm font-medium text-muted-foreground">Fallback Rate</span>
              </div>
              <div className="text-2xl font-bold">{metrics.fallbackRate}%</div>
              <p className="text-xs text-muted-foreground mt-1">Zone fallback usage</p>
            </div>
            <div className="p-4 border border-border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-4 w-4 text-destructive" />
                <span className="text-sm font-medium text-muted-foreground">Arabic Detections</span>
              </div>
              <div className="text-2xl font-bold">{metrics.arabicDetections}</div>
              <p className="text-xs text-muted-foreground mt-1">Today's count</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
